<template>
  <form class="ds-container" @submit.prevent="save">
    <!-- Basic Information -->
    <div class="form-card" v-if="sections.includes('basic')">
      <div class="form-card__header">
        <div class="form-card__header-icon"><i class="pi pi-info-circle"></i></div>
        <div>
          <p class="form-card__title">البيانات الأساسية</p>
          <p class="form-card__subtitle">اسم الكورس والسنة الدراسية المرتبطة به</p>
        </div>
      </div>
      <div class="form-card__body">
        <div class="ds-field-grid">
          <div class="form-field ds-field--full">
            <label class="field-label">اسم الكورس <span class="required">*</span></label>
            <input type="text" class="form-control" v-model="form.title" placeholder="مثال: مراجعة نهائية جبر" />
          </div>
          <div class="form-field">
            <label class="field-label">السنة الدراسية <span class="optional">اختياري</span></label>
            <select class="form-select" v-model="form.academic_year_id">
              <option :value="null">بدون سنة دراسية</option>
              <option v-for="y in academicYears" :key="y.id" :value="y.id">{{ y.name }}</option>
            </select>
          </div>
          <div class="form-field">
            <label class="field-label">الترتيب <span class="optional">اختياري</span></label>
            <input type="number" class="form-control" v-model="form.sort_order" min="0" placeholder="0" dir="ltr" />
          </div>
          <div class="form-field ds-field--full">
            <label class="field-label">الوصف <span class="optional">اختياري</span></label>
            <textarea class="form-control" rows="3" v-model="form.description" placeholder="وصف مختصر يظهر للطالب..."></textarea>
          </div>
        </div>
      </div>
    </div>

    <ProductImagesSection v-if="sections.includes('images')" :preview="preview" @file-change="onImage" />

    <ProductPricingSection v-if="sections.includes('pricing')" :form="form" />

    <ProductSettingsSection
      v-if="sections.includes('settings')"
      :form="form"
      subtitle="التحكم في ظهور الكورس واشتراكه"
      visible-desc="يظهر الكورس في صفحة الكورسات العامة"
      home-desc="يظهر الكورس ضمن الكورسات المميزة بالصفحة الرئيسية"
    />

    <div class="form-actions form-actions--sticky">
      <button type="submit" class="btn-submit" :disabled="store.saving || !form.title.trim()">
        <i class="pi pi-spin pi-spinner-dotted" v-if="store.saving"></i>
        <i class="pi pi-check" v-else></i>
        {{ store.saving ? 'جارٍ الحفظ...' : 'حفظ الكورس' }}
      </button>
      <button type="button" class="btn-cancel" @click="$router.push('/courses')">إلغاء</button>
    </div>
  </form>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import ProductImagesSection from '@/views/shared/ProductImagesSection.vue';
import ProductPricingSection from '@/views/shared/ProductPricingSection.vue';
import ProductSettingsSection from '@/views/shared/ProductSettingsSection.vue';
import { useCourseStore } from '@/stores/useCourseStore.js';
import { apiError } from '@/composables/subscriptionMeta.js';

function emptyForm() {
  return {
    title: '', description: '', academic_year_id: null, sort_order: 0,
    monthly_price: '', term1_price: '', term2_price: '', yearly_price: '',
    term1_end_date: '', term2_end_date: '',
    is_visible: true, is_subscription_enabled: false, show_on_home: false,
  };
}

export default {
  name: 'CourseForm',
  components: { ProductImagesSection, ProductPricingSection, ProductSettingsSection },
  emits: ['saved'],
  props: {
    course: { type: Object, default: null },
    sections: { type: Array, default: () => ['basic', 'images', 'pricing', 'settings'] },
    redirectTo: { type: String, default: '/courses' },
  },
  data() {
    return {
      form: emptyForm(),
      files: { image: null, cover_image: null },
      preview: { image: null, cover_image: null },
      academicYears: [],
    };
  },
  computed: {
    store() { return useCourseStore(); },
  },
  watch: {
    course: {
      immediate: true,
      handler(c) {
        if (!c) return;
        this.form = {
          title: c.title ?? '', description: c.description ?? '',
          academic_year_id: c.academic_year_id ?? null, sort_order: c.sort_order ?? 0,
          monthly_price: c.monthly_price ?? '', term1_price: c.term1_price ?? '',
          term2_price: c.term2_price ?? '', yearly_price: c.yearly_price ?? '',
          term1_end_date: c.term1_end_date ? String(c.term1_end_date).slice(0, 10) : '',
          term2_end_date: c.term2_end_date ? String(c.term2_end_date).slice(0, 10) : '',
          is_visible: !!c.is_visible, is_subscription_enabled: !!c.is_subscription_enabled,
          show_on_home: !!c.show_on_home,
        };
        this.preview = { image: c.image_link, cover_image: c.cover_image_link };
      },
    },
  },
  methods: {
    onImage(field, file) {
      this.files[field] = file;
      this.preview[field] = URL.createObjectURL(file);
    },
    buildFormData() {
      const fd = new FormData();
      const f = this.form;
      fd.append('title', f.title.trim());
      fd.append('description', f.description ?? '');
      if (f.academic_year_id) fd.append('academic_year_id', f.academic_year_id);
      fd.append('sort_order', f.sort_order || 0);
      ['monthly_price', 'term1_price', 'term2_price', 'yearly_price'].forEach(k =>
        fd.append(k, f[k] === '' ? '' : f[k]));
      fd.append('term1_end_date', f.term1_end_date ?? '');
      fd.append('term2_end_date', f.term2_end_date ?? '');
      fd.append('is_visible', f.is_visible ? 1 : 0);
      fd.append('is_subscription_enabled', f.is_subscription_enabled ? 1 : 0);
      fd.append('show_on_home', f.show_on_home ? 1 : 0);
      if (this.files.image) fd.append('image', this.files.image);
      if (this.files.cover_image) fd.append('cover_image', this.files.cover_image);
      return fd;
    },
    async save() {
      try {
        const fd = this.buildFormData();
        let saved;
        if (this.course) {
          saved = await this.store.updateCourse(this.course.id, fd);
        } else {
          saved = await this.store.createCourse(fd);
        }
        await Swal.fire({ icon: 'success', title: 'تم الحفظ', timer: 1200, showConfirmButton: false });
        this.$emit('saved', saved);
        if (this.redirectTo) this.$router.push(this.redirectTo);
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
    async loadAcademicYears() {
      try {
        const { data } = await axios.get('/academic-years');
        this.academicYears = data.data ?? [];
      } catch { /* non-fatal */ }
    },
  },
  mounted() {
    this.loadAcademicYears();
  },
};
</script>
